import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  User, Lightbulb, Check, X, Clock, Building2, Mail, 
  Calendar, Edit2, Save, AlertCircle, Target, Star, Send, Trophy, Medal, Award
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import NotificationSettings from "../components/NotificationSettings";

const DEPARTMENTS = {
  sales: { label: "Sales", color: "bg-indigo-600", textColor: "text-indigo-400" },
  csm: { label: "Customer Success", color: "bg-emerald-600", textColor: "text-emerald-400" },
  dev: { label: "Development", color: "bg-purple-600", textColor: "text-purple-400" },
  support: { label: "Support", color: "bg-amber-600", textColor: "text-amber-400" },
  marketing: { label: "Marketing", color: "bg-pink-600", textColor: "text-pink-400" },
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Department-specific metrics
const DEPT_METRICS = {
  sales: [
    { key: "team_generated", label: "Team Generated", placeholder: "0" },
    { key: "self_generated", label: "Self Generated", placeholder: "0" },
    { key: "demos_held", label: "Demos Held", placeholder: "0" },
    { key: "upgrades_closed", label: "Upgrades Closed", placeholder: "0" },
    { key: "new_deals_closed", label: "New Deals Closed", placeholder: "0" },
    { key: "deals_revenue", label: "Deals Revenue ($)", placeholder: "0.00", isMoney: true },
  ],
  csm: [
    { key: "renewals", label: "Renewals", placeholder: "0" },
    { key: "renewals_revenue", label: "Renewals Revenue ($)", placeholder: "0.00", isMoney: true },
    { key: "upsells", label: "Upsells", placeholder: "0" },
    { key: "upsells_revenue", label: "Upsells Revenue ($)", placeholder: "0.00", isMoney: true },
    { key: "customer_calls", label: "Customer Calls", placeholder: "0" },
    { key: "nps_score", label: "NPS Score", placeholder: "0" },
    { key: "churn_prevention", label: "Churn Prevention", placeholder: "0" },
  ],
  dev: [
    { key: "tickets_closed", label: "Tickets Closed", placeholder: "0" },
    { key: "features_shipped", label: "Features Shipped", placeholder: "0" },
    { key: "bugs_fixed", label: "Bugs Fixed", placeholder: "0" },
    { key: "code_reviews", label: "Code Reviews", placeholder: "0" },
    { key: "deployments", label: "Deployments", placeholder: "0" },
  ],
  support: [
    { key: "tickets_resolved", label: "Tickets Resolved", placeholder: "0" },
    { key: "first_response_time", label: "Avg Response (min)", placeholder: "0" },
    { key: "customer_satisfaction", label: "CSAT Score", placeholder: "0" },
    { key: "escalations_handled", label: "Escalations", placeholder: "0" },
    { key: "knowledge_articles", label: "KB Articles", placeholder: "0" },
  ],
  marketing: [
    { key: "leads_generated", label: "Leads Generated", placeholder: "0" },
    { key: "campaigns_launched", label: "Campaigns", placeholder: "0" },
    { key: "content_published", label: "Content Published", placeholder: "0" },
    { key: "social_engagement", label: "Social Engagement", placeholder: "0" },
    { key: "email_opens", label: "Email Opens", placeholder: "0" },
  ],
};

const Profile = () => {
  const { token, user, isAdmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const fileInputRef = useRef(null);
  
  // Admin: Select user for data entry
  const [allSalespeople, setAllSalespeople] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("self"); // "self" or salesperson id
  
  // Data Entry State
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [weekOptions, setWeekOptions] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [saving, setSaving] = useState(false);
  const [companyRocks, setCompanyRocks] = useState([]);
  const [monthlyEntries, setMonthlyEntries] = useState([]); // All entries for selected month
  const [editingEntryId, setEditingEntryId] = useState(null); // Track which entry is being edited
  
  // Rock Suggestion State
  const [newIdea, setNewIdea] = useState("");
  const [submittingIdea, setSubmittingIdea] = useState(false);
  
  // User stats (tenure, awards)
  const [userStats, setUserStats] = useState({ tenure_text: "", tenure_days: 0, awards: [] });

  const headers = { Authorization: `Bearer ${token}` };

  // Get the selected user's department for data entry
  const getSelectedUserDept = () => {
    if (!selectedUserId || selectedUserId === "self") {
      return user?.department || profile?.user?.department || "sales";
    }
    const selectedPerson = allSalespeople?.find(sp => sp.id === selectedUserId);
    return selectedPerson?.department || "sales";
  };

  // Get user department and metrics
  const userDept = user?.department || profile?.user?.department || "sales";
  const deptConfig = DEPARTMENTS[userDept] || DEPARTMENTS.sales;
  
  // For data entry, use selected user's department
  const entryDept = getSelectedUserDept();
  const entryDeptConfig = DEPARTMENTS[entryDept] || DEPARTMENTS.sales;
  const currentMetrics = DEPT_METRICS[entryDept] || DEPT_METRICS.sales || [];

  useEffect(() => {
    fetchProfile();
    fetchCompanyRocks();
    fetchUserStats();
    if (isAdmin) {
      fetchAllSalespeople();
    }
  }, []);

  useEffect(() => {
    generateWeekOptions();
  }, [selectedYear, selectedMonth]);

  // Ensure a week is always selected when options are available
  useEffect(() => {
    if (weekOptions.length > 0 && !selectedWeek) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      
      if (selectedYear === currentYear && selectedMonth === currentMonth) {
        // Find the most recent Friday that's not in the future
        const todayStr = today.toISOString().split("T")[0];
        let defaultWeek = weekOptions[0].value;
        
        for (const week of weekOptions) {
          if (week.value <= todayStr) {
            defaultWeek = week.value;
          } else {
            break;
          }
        }
        setSelectedWeek(defaultWeek);
      } else {
        setSelectedWeek(weekOptions[0].value);
      }
    }
  }, [weekOptions, selectedWeek, selectedYear, selectedMonth]);

  useEffect(() => {
    let employeeId;
    if (selectedUserId === "self") {
      employeeId = profile?.employee?.id || profile?.user?.id;
    } else {
      employeeId = selectedUserId;
    }
    if (employeeId && selectedWeek) {
      fetchExistingEntry(employeeId);
      // Clear any "Please select a week" error when week is selected
      setMessage(prev => prev.text === "Please select a week" ? { type: "", text: "" } : prev);
    }
  }, [profile, selectedWeek, selectedUserId]);

  // Reset metrics when changing user (but keep the week selection)
  const prevSelectedUserId = useRef(selectedUserId);
  useEffect(() => {
    // Only reset metrics if user actually changed, not on initial salespeople load
    if (prevSelectedUserId.current !== selectedUserId) {
      const deptMetrics = DEPT_METRICS[getSelectedUserDept()] || DEPT_METRICS.sales || [];
      if (deptMetrics.length > 0) {
        const newMetrics = {};
        deptMetrics.forEach(m => {
          newMetrics[m.key] = "";
        });
        setMetrics(newMetrics);
      }
      // Don't clear selectedWeek - keep the current week selected when switching users
      prevSelectedUserId.current = selectedUserId;
    }
  }, [selectedUserId]);

  const fetchAllSalespeople = async () => {
    try {
      const res = await axios.get(`${API}/salespeople`, { headers });
      setAllSalespeople(res.data);
    } catch (error) {
      console.error("Error fetching salespeople:", error);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profile`, { headers });
      setProfile(res.data);
      setEditName(res.data.user?.name || "");
      
      // Initialize metrics
      const initialMetrics = {};
      const dept = res.data.user?.department || "sales";
      const deptMetrics = DEPT_METRICS[dept] || DEPT_METRICS.sales;
      deptMetrics.forEach(m => { initialMetrics[m.key] = ""; });
      setMetrics(initialMetrics);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const res = await axios.get(`${API}/profile/stats`, { headers });
      setUserStats(res.data);
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  };

  const fetchCompanyRocks = async () => {
    try {
      const dept = user?.department || "sales";
      const res = await axios.get(`${API}/company-rocks?month=${selectedMonth}&year=${selectedYear}&department=${dept}`, { headers });
      setCompanyRocks(res.data);
    } catch (error) {
      console.error("Error fetching rocks:", error);
    }
  };

  const generateWeekOptions = () => {
    const weeks = [];
    const year = selectedYear;
    const month = selectedMonth - 1;
    
    let date = new Date(year, month, 1);
    const dayOfWeek = date.getDay();
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    date.setDate(date.getDate() + daysUntilFriday);
    
    if (date.getMonth() !== month) {
      date.setDate(date.getDate() + 7);
    }
    
    while (date.getMonth() === month) {
      weeks.push({
        value: date.toISOString().split("T")[0],
        label: `Fri, ${MONTHS[date.getMonth()].substring(0, 3)} ${date.getDate()}`,
      });
      date.setDate(date.getDate() + 7);
    }
    
    setWeekOptions(weeks);
    
    // Default to current week (most recent Friday) if in current month
    // or the most recent week if viewing current month
    if (weeks.length > 0) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      
      if (year === currentYear && selectedMonth === currentMonth) {
        // Find the most recent Friday that's not in the future
        const todayStr = today.toISOString().split("T")[0];
        let defaultWeek = weeks[0].value;
        
        for (const week of weeks) {
          if (week.value <= todayStr) {
            defaultWeek = week.value;
          } else {
            break;
          }
        }
        setSelectedWeek(defaultWeek);
      } else {
        // For past/future months, default to first week
        setSelectedWeek(weeks[0].value);
      }
    }
  };

  const fetchExistingEntry = async (employeeId) => {
    if (!employeeId) return;
    
    try {
      const response = await axios.get(
        `${API}/entries?salesperson_id=${employeeId}&year=${selectedYear}&month=${selectedMonth}`,
        { headers }
      );
      
      // Store all entries for the month
      setMonthlyEntries(response.data || []);
      
      const existingEntry = response.data.find(
        (entry) => entry.week_ending_date === selectedWeek
      );
      
      if (existingEntry) {
        const loadedMetrics = {};
        currentMetrics.forEach(m => {
          loadedMetrics[m.key] = existingEntry.metrics[m.key]?.toString() || "";
        });
        setMetrics(loadedMetrics);
        setEditingEntryId(existingEntry.id);
      } else {
        const emptyMetrics = {};
        currentMetrics.forEach(m => { emptyMetrics[m.key] = ""; });
        setMetrics(emptyMetrics);
        setEditingEntryId(null);
      }
    } catch (error) {
      console.error("Error fetching existing entry:", error);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file" });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/profile/avatar`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });

      setProfile(prev => ({
        ...prev,
        user: { ...prev.user, avatar: res.data.avatar },
        employee: prev.employee ? { ...prev.employee, avatar_url: res.data.avatar } : null
      }));

      setMessage({ type: "success", text: "Profile picture updated!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to upload image" });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;

    try {
      await axios.put(`${API}/profile?name=${encodeURIComponent(editName)}`, {}, { headers });
      setProfile(prev => ({
        ...prev,
        user: { ...prev.user, name: editName },
        employee: prev.employee ? { ...prev.employee, name: editName } : null
      }));
      setEditing(false);
      setMessage({ type: "success", text: "Name updated!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update name" });
    }
  };

  const handleMetricChange = (key, value) => {
    setMetrics((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    
    // Get the employee ID - either selected user or self
    // For admins without an employee record, use their user ID
    let employeeId;
    if (selectedUserId === "self") {
      employeeId = profile?.employee?.id || profile?.user?.id;
    } else {
      employeeId = selectedUserId;
    }
    
    console.log("Save Entry Debug:", { employeeId, selectedWeek, weekOptions: weekOptions.length });
    
    if (!employeeId || !selectedWeek) {
      console.log("Validation failed - employeeId:", employeeId, "selectedWeek:", selectedWeek);
      setMessage({ type: "error", text: "Please select a week" });
      return;
    }

    setSaving(true);
    try {
      const metricsPayload = {};
      (currentMetrics || []).forEach(m => {
        metricsPayload[m.key] = parseFloat(metrics[m.key]) || 0;
      });

      const payload = {
        salesperson_id: employeeId,
        week_ending_date: selectedWeek,
        year: selectedYear,
        month: selectedMonth,
        metrics: metricsPayload,
      };

      await axios.post(`${API}/entries`, payload, { headers });
      
      // Show which user the data was saved for
      const savedUserName = selectedUserId === "self" 
        ? "your" 
        : (allSalespeople?.find(sp => sp.id === selectedUserId)?.name || "user") + "'s";
      setMessage({ type: "success", text: `✓ Entry saved for ${savedUserName} scorecard!` });
      
      // Clear the form after successful save
      const emptyMetrics = {};
      (currentMetrics || []).forEach(m => { emptyMetrics[m.key] = ""; });
      setMetrics(emptyMetrics);
      
      // Move to next week if available
      const currentWeekIndex = weekOptions.findIndex(w => w.value === selectedWeek);
      if (currentWeekIndex < weekOptions.length - 1) {
        setSelectedWeek(weekOptions[currentWeekIndex + 1].value);
      }
      
      // Refresh the monthly entries list
      fetchExistingEntry(employeeId);
      
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setMessage({ type: "error", text: "Failed to save entry" });
    } finally {
      setSaving(false);
    }
  };

  // Handle clicking edit on a saved entry
  const handleEditEntry = (entry) => {
    // Set the week to the entry's week
    setSelectedWeek(entry.week_ending_date);
    setEditingEntryId(entry.id);
    
    // Load the entry's metrics into the form
    const loadedMetrics = {};
    currentMetrics.forEach(m => {
      loadedMetrics[m.key] = entry.metrics[m.key]?.toString() || "";
    });
    setMetrics(loadedMetrics);
    
    // Scroll to the form
    document.getElementById('scorecard-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmitIdea = async () => {
    if (!newIdea.trim()) return;
    
    setSubmittingIdea(true);
    try {
      await axios.post(`${API}/rock-ideas`, {
        idea: newIdea,
        department: userDept,
        month: selectedMonth,
        year: selectedYear,
      }, { headers });
      
      setNewIdea("");
      setMessage({ type: "success", text: "Rock suggestion submitted!" });
      fetchProfile(); // Refresh to show new idea
    } catch (error) {
      setMessage({ type: "error", text: "Failed to submit idea" });
    } finally {
      setSubmittingIdea(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "text-emerald-400 bg-emerald-500/20";
      case "rejected": return "text-red-400 bg-red-500/20";
      default: return "text-amber-400 bg-amber-500/20";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <Check size={12} />;
      case "rejected": return <X size={12} />;
      default: return <Clock size={12} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const avatar = profile?.user?.avatar || profile?.employee?.avatar_url;

  return (
    <div className="animate-fade-in max-w-2xl" data-testid="profile-page">
      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-2 rounded-lg text-sm flex items-center gap-2 ${
          message.type === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
        }`}>
          {message.type === "success" ? <Check size={14} /> : <AlertCircle size={14} />}
          {message.text}
          <button onClick={() => setMessage({ type: "", text: "" })} className="ml-auto">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Profile Header Card */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-4">
          {/* Avatar Initial */}
          <div className={`w-16 h-16 rounded-xl ${deptConfig.color} flex items-center justify-center`}>
            <span className="text-white text-2xl font-bold">
              {profile?.user?.name?.charAt(0) || "?"}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1">
            {editing ? (
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-8 bg-slate-900 border-slate-600 text-white max-w-[180px] text-sm"
                />
                <Button size="sm" onClick={handleSaveName} className="h-8 bg-emerald-600 hover:bg-emerald-700">
                  <Save size={12} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8">
                  <X size={12} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-lg font-bold text-white">{profile?.user?.name}</h1>
                <button onClick={() => setEditing(true)} className="text-slate-500 hover:text-slate-300">
                  <Edit2 size={12} />
                </button>
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
              <div className="flex items-center gap-1">
                <Mail size={12} />
                <span>{profile?.user?.email}</span>
              </div>
              <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${deptConfig.color}`}>
                <Building2 size={10} className="text-white" />
                <span className="text-white text-xs font-medium">{deptConfig.label}</span>
              </div>
            </div>
            
            {/* Tenure & Join Date */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
              {userStats.join_date && (
                <div className="flex items-center gap-1 text-slate-400">
                  <Calendar size={12} />
                  <span>Joined {userStats.join_date}</span>
                </div>
              )}
              {userStats.tenure_text && userStats.tenure_text !== "N/A" && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 rounded text-emerald-400">
                  <Clock size={12} />
                  <span>{userStats.tenure_text}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Awards Section */}
        {userStats.awards && userStats.awards.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-yellow-400" />
              <span className="text-xs font-medium text-slate-300">Awards</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {userStats.awards.map((award, idx) => (
                <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  {award.rank === 1 && <Trophy size={12} className="text-yellow-400" />}
                  {award.rank === 2 && <Medal size={12} className="text-slate-300" />}
                  {award.rank === 3 && <Award size={12} className="text-amber-600" />}
                  <span className="text-xs text-slate-300">
                    {award.rank === 1 ? "1st" : award.rank === 2 ? "2nd" : "3rd"} - {new Date(2000, award.month - 1).toLocaleString('default', { month: 'short' })} {award.year}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Weekly Data Entry Section */}
      <div id="scorecard-form" className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Target size={16} className={entryDeptConfig?.textColor || "text-indigo-400"} />
          Weekly Scorecard Entry
          {editingEntryId && (
            <span className="text-xs font-normal text-amber-400 ml-2">
              (Editing existing entry)
            </span>
          )}
          {isAdmin && selectedUserId !== "self" && !editingEntryId && (
            <span className="text-xs font-normal text-amber-400 ml-2">
              (Admin Mode)
            </span>
          )}
        </h2>

        {/* Admin: User Selector */}
        {isAdmin && (
          <div className="mb-3 p-2 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <Label className="text-[10px] text-amber-400 mb-1 block">Enter data for:</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="h-8 text-xs bg-slate-900 border-amber-500/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
                <SelectItem value="self" className="text-slate-200 text-xs">
                  Myself ({user?.name})
                </SelectItem>
                {allSalespeople
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(sp => (
                    <SelectItem key={sp.id} value={sp.id} className="text-slate-200 text-xs">
                      {sp.name} - {DEPARTMENTS[sp.department]?.label || sp.department}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Check if user can enter data for themselves - show warning only if no employee AND no user ID */}
        {selectedUserId === "self" && !profile?.employee?.id && !profile?.user?.id && !isAdmin && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
            <p className="text-xs text-amber-400">Your account is not linked to a scorecard profile yet.</p>
            <p className="text-[10px] text-slate-500 mt-1">Please contact your admin to set this up.</p>
          </div>
        )}

        {/* Show data entry form if user has employee record OR user ID OR admin selected someone else */}
        {((selectedUserId === "self" && (profile?.employee?.id || profile?.user?.id)) || (isAdmin && selectedUserId !== "self") || (isAdmin && allSalespeople?.length > 0)) && (
          <>
            {/* Current Rocks */}
            {companyRocks.length > 0 && (
              <div className="mb-3 p-2 bg-slate-900/50 rounded-lg border border-slate-700/30">
                <p className="text-[10px] text-slate-500 mb-1">{MONTHS[selectedMonth - 1]} Goals</p>
                <div className="flex flex-wrap gap-1">
                  {companyRocks.slice(0, 3).map(rock => (
                    <span key={rock.id} className="text-[10px] px-2 py-0.5 bg-slate-800 text-slate-300 rounded">
                      {rock.title}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Period Selection */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <Label className="text-[10px] text-slate-500">Month</Label>
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="h-8 text-xs bg-slate-900 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={month} value={(idx + 1).toString()} className="text-slate-200 text-xs">{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Year</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="h-8 text-xs bg-slate-900 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()} className="text-slate-200 text-xs">{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Week Ending</Label>
                <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                  <SelectTrigger className="h-8 text-xs bg-slate-900 border-slate-700">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {weekOptions.map(week => (
                      <SelectItem key={week.value} value={week.value} className="text-slate-200 text-xs">{week.label}</SelectItem>
                    ))}
                  </SelectContent>
            </Select>
          </div>
        </div>

        {/* Metrics Grid - Compact inputs for 3-4 digit numbers */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {(currentMetrics || []).map(metric => (
            <div key={metric.key}>
              <Label className={`text-[10px] ${metric.isMoney ? "text-emerald-400" : "text-slate-500"} truncate block`}>
                {metric.isMoney ? metric.label.replace(" ($)", "") : metric.label}
              </Label>
              <div className="relative">
                {metric.isMoney && (
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-emerald-400 text-[10px]">$</span>
                )}
                <Input
                  type="number"
                  step={metric.isMoney ? "0.01" : "1"}
                  min="0"
                  placeholder={metric.placeholder || "0"}
                  value={metrics[metric.key] || ""}
                  onChange={(e) => handleMetricChange(metric.key, e.target.value)}
                  className={`h-7 text-xs font-mono bg-slate-900 border-slate-700 px-1.5 ${
                    metric.isMoney ? "pl-4 text-emerald-400 border-emerald-500/30" : ""
                  }`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSaveEntry}
          className={`w-full h-9 text-sm ${deptConfig?.color || "text-indigo-400"} hover:opacity-90`}
          disabled={saving}
        >
          {saving ? (
            <><div className="loading-spinner w-3 h-3 mr-2"></div>Saving...</>
          ) : (
            <><Save className="mr-2 h-3 w-3" />{editingEntryId ? 'Update Entry' : 'Save Entry'}</>
          )}
        </Button>
        
        {/* Saved Entries for this Month */}
        {monthlyEntries.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <h3 className="text-xs font-semibold text-slate-400 mb-3 flex items-center gap-2">
              <Calendar size={14} />
              Saved Entries for {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
            </h3>
            <div className="space-y-2">
              {monthlyEntries.map(entry => {
                const entryTotal = Object.entries(entry.metrics || {})
                  .filter(([key]) => !['deals_revenue', 'renewals_revenue', 'upsells_revenue'].includes(key))
                  .reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
                const weekDate = new Date(entry.week_ending_date);
                const isCurrentEdit = entry.id === editingEntryId;
                
                return (
                  <div 
                    key={entry.id} 
                    className={`flex items-center justify-between p-2 rounded-lg border ${
                      isCurrentEdit 
                        ? 'bg-indigo-500/20 border-indigo-500/50' 
                        : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-slate-400">
                        Week of {weekDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="flex gap-2 text-xs">
                        {currentMetrics.slice(0, 4).map(m => (
                          <span key={m.key} className="text-slate-500">
                            {m.shortLabel || m.label.split(' ')[0]}: <span className="text-slate-300">{entry.metrics[m.key] || 0}</span>
                          </span>
                        ))}
                      </div>
                      <div className={`text-sm font-bold ${deptConfig?.textColor || 'text-indigo-400'}`}>
                        Total: {entryTotal}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={isCurrentEdit ? "default" : "outline"}
                      className={isCurrentEdit ? "bg-indigo-600 h-7 px-3" : "h-7 px-3 border-slate-600 hover:bg-slate-700"}
                      onClick={() => handleEditEntry(entry)}
                    >
                      <Edit2 size={12} className="mr-1" />
                      {isCurrentEdit ? 'Editing' : 'Edit'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Rock Suggestion Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Lightbulb size={16} className="text-amber-400" />
          Suggest a Rock
        </h2>
        
        <div className="flex gap-2">
          <Textarea
            value={newIdea}
            onChange={(e) => setNewIdea(e.target.value)}
            placeholder="Share your idea for a team goal or rock..."
            className="min-h-[60px] text-sm bg-slate-900 border-slate-700 resize-none"
          />
          <Button
            onClick={handleSubmitIdea}
            disabled={submittingIdea || !newIdea.trim()}
            className="bg-amber-600 hover:bg-amber-700 h-auto px-3"
          >
            {submittingIdea ? <div className="loading-spinner w-4 h-4"></div> : <Send size={16} />}
          </Button>
        </div>
      </div>

      {/* My Submitted Ideas */}
      {profile?.ideas?.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
          <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <Star size={16} className="text-slate-400" />
            My Rock Suggestions
            <span className="text-xs text-slate-500">({profile.ideas.length})</span>
          </h2>

          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {profile.ideas.map(idea => (
              <div key={idea.id} className="p-2 bg-slate-900/50 border border-slate-700/50 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-slate-300 flex-1">{idea.idea}</p>
                  <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${getStatusColor(idea.status)}`}>
                    {getStatusIcon(idea.status)}
                    {idea.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notification Settings */}
      <NotificationSettings />
    </div>
  );
};

export default Profile;
